// ADR 5.9.26i §1 — the HTTP seam. ADR 5.9.26p §4 — its authenticated half.
//
// 5.9.26i left a base URL, a JSON helper and the error envelope the backend
// already speaks, with `headers` as the seam bearer tokens would hang off.
// This is that half: `authedJson` injects the bearer, and on a 401 refreshes
// once and replays the request once — `try_refresh_and_retry` in
// `rust-core/src/client/http.rs`, with the same never-loop rule.
//
// The layering is deliberate and one-directional: `auth/tokenStore.ts` owns
// *when* a refresh may happen and *what its failure means*, and this module
// owns the request that performs it. So the transport is injected downwards
// (`refreshTransport` below) rather than imported upwards, and there is no
// cycle between the two.
//
// The browser calls the API origin directly — `VITE_API_BASE_URL`, named in
// 5.9.26k's `connect-src`. There is no same-origin proxy: a proxy would only
// launder the CORS decision 5.9.26j makes explicitly, and hide it from anyone
// reading the network tab.

import { readDeviceToken } from '../auth/deviceStore'
import {
  ensureFreshToken,
  refreshAfterUnauthorized,
  refreshTokens,
  signOutLocally,
  type RefreshTransport,
} from '../auth/tokenStore'
import { appLanguageHeader } from '../i18n/localeStore'
import type { AuthResponse } from './auth'
import { anonymousRequest } from './deviceReplay'
import { fetchOrUnreachable } from '../offline/networkFailure'

const BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? ''

const UNAUTHORIZED = 401

/**
 * ADR 11.9.26i §5 — the one bit the backend sets when it refused the device
 * token this request carried. `7.8.26c` wrote it for a *successful* fallback
 * response; 11.9.26i extends it to the token-only 400, and `cors.rs` exposes
 * it so a browser can read it at all.
 */
const DEVICE_TOKEN_REJECTED = 'x-device-token-rejected'

/** The header an anonymous request names itself with (§6). Never on `authed`. */
export const DEVICE_TOKEN_HEADER = 'X-Device-Token'

/**
 * The backend's error body: `{"code":"safety_input_flagged","error":"…"}` on
 * the typed paths, `{"detail":"…"}` on others. Both are optional — a 502 from
 * a proxy in front of the API carries neither.
 */
export interface ApiErrorBody {
  code?: string
  error?: string
  detail?: string
  redirect?: string // Backend 25.9.26n §3 — a redirectable OAuth refusal's error redirect.
}

/** A non-2xx response, carrying enough to branch on without re-reading the body. */
export class ApiError extends Error {
  readonly status: number
  readonly code?: string
  readonly redirect?: string
  /**
   * §5 — the response said the device token it was sent is refused. Read off
   * the headers before the throw, because the headers do not survive it, and
   * this is the *only* signal worth branching on: the 400 body is a translated
   * sentence with no `code`.
   */
  readonly deviceTokenRejected: boolean
  /**
   * ADR 15.9.26o §1 — `Retry-After` as a non-negative integer of seconds,
   * read before the throw for the reason above. Delta-seconds only: the
   * backend emits integers (15.9.26m). Readable cross-origin only because
   * `cors.rs` exposes it — pinned in the Backend's `cors_tests.rs`, not here.
   */
  readonly retryAfterSecs?: number

  constructor(
    status: number, body: ApiErrorBody, fallback: string, deviceTokenRejected = false, retryAfterSecs?: number,
  ) {
    super(body.error ?? body.detail ?? fallback)
    this.name = 'ApiError'
    this.status = status
    this.code = body.code
    this.redirect = body.redirect
    this.deviceTokenRejected = deviceTokenRejected
    this.retryAfterSecs = retryAfterSecs
  }
}

/** §1's parse: `"7"` → 7; `"-3"`, `"7.5"`, an HTTP-date or no header → `undefined`. */
export function retryAfterSeconds(header: string | null): number | undefined {
  if (header === null || !/^\d+$/.test(header.trim())) return undefined
  return Number(header)
}

export interface RequestOptions {
  method?: string
  body?: unknown
  headers?: Record<string, string>
  signal?: AbortSignal
  timeoutMs?: number // ADR 2.10.26s §1 — a per-request ceiling override (1.8.26e D5); default 20 s.
  /**
   * ADR 5.9.26t §1 — send this request as a stranger would.
   *
   * No bearer, and no 401 → refresh → retry: the public shared-plan read is a
   * route where 401 is an *answer* about the credential, not about the
   * resource, and refreshing behind it would turn a stale token into a session
   * the reader never asked to resume. `authed` honours it so a caller can pick
   * per request rather than picking a different function, which is what lets
   * one query key decide (`SharedPlanPage`) instead of two call sites.
   */
  anonymous?: boolean
}

export function apiUrl(path: string): string {
  return `${BASE_URL}${path}`
}

/**
 * Who is calling, and in which language — spread last at both call sites.
 *
 * ADR 6.9.26c §3: `X-Client-Platform` is 16.8.26b D5's header, which `rust-core`
 * sets on both native builders and the backend reads at
 * `routes/tutor/platform.rs`; the web joins that vocabulary rather than minting
 * another. **Bare `web`, no version**: the header is caller-controlled and
 * echoed into a Prometheus label, so a version would let a caller mint time
 * series (a version belongs on a PostHog property). It is **not**
 * CORS-safelisted, so every request is preflighted — `x-client-platform` in the
 * backend's `allowed_headers` is what lets the browser send one at all.
 *
 * ADR 30.9.26d §4: `Accept-Language` is the app language's catalogue code, as
 * rust-core sends the app locale, so server sentences match the screen. Absent
 * before `initI18n` (the browser's own applies).
 */
export function clientHeaders(): Record<string, string> {
  const language = appLanguageHeader()
  return language === null
    ? { 'X-Client-Platform': 'web' }
    : { 'X-Client-Platform': 'web', 'Accept-Language': language }
}

/**
 * ADR 11.9.26i §6 — the identity a stranger has, when it has one.
 *
 * Attached only on `anonymous: true` and only when the store holds a token;
 * nothing otherwise. Callers that *need* the identity await
 * `ensureDeviceToken()` first; callers that do not (the shared-plan reader)
 * are unchanged, and a stored token riding their read is accepted — the route
 * never resolves a tier for it. An `authed` request never reaches this branch
 * (AC10): a bearer and a device token on one request is a correlation between
 * a user and an anonymous subject that no log line should be able to make.
 */
function deviceHeaders(options: RequestOptions): Record<string, string> {
  if (options.anonymous !== true) return {}
  const token = readDeviceToken()
  return token === null ? {} : { [DEVICE_TOKEN_HEADER]: token }
}

function requestInit(options: RequestOptions): RequestInit {
  const { method = 'GET', body, headers = {}, signal } = options
  // `clientHeaders()` is spread **last** in both branches, so a caller cannot
  // shadow it — deliberately the opposite of the `Content-Type` merge below,
  // which lets a caller override on purpose. A bodyless request (every GET)
  // takes the first branch.
  const withDevice = { ...headers, ...deviceHeaders(options) }
  // ADR 30.9.26ai §5 — a `Blob` (an app icon's PNG) goes as its own bytes and type, never JSON.
  if (body instanceof Blob) return { method, signal, body, headers: { 'Content-Type': body.type, ...withDevice, ...clientHeaders() } }
  return {
    method,
    signal,
    headers:
      body === undefined
        ? { ...withDevice, ...clientHeaders() }
        : { 'Content-Type': 'application/json', ...withDevice, ...clientHeaders() },
    body: body === undefined ? undefined : JSON.stringify(body),
  }
}

/**
 * Read a non-2xx body as the backend's error envelope.
 *
 * Exported for `sse.ts`, whose `headers` promise rejects with the *raw* body
 * rather than an `ApiError`: a 429 on a stream carries `X-RateLimit-*` the
 * caller still wants, so it branches on the pair rather than on a message.
 */
export async function errorBody(response: Response): Promise<ApiErrorBody> {
  try {
    return (await response.json()) as ApiErrorBody
  } catch {
    // A body that is not JSON tells us nothing the status has not; the caller
    // gets the status-line fallback rather than a parse error about a parse error.
    return {}
  }
}

/** Fetch `path`, throwing `ApiError` on any non-2xx. */
export async function request(path: string, options: RequestOptions = {}): Promise<Response> {
  const response = await fetchOrUnreachable(apiUrl(path), requestInit(options), options.timeoutMs)
  if (!response.ok) {
    throw new ApiError(
      response.status,
      await errorBody(response),
      response.statusText,
      response.headers.has(DEVICE_TOKEN_REJECTED),
      retryAfterSeconds(response.headers.get('Retry-After')),
    )
  }
  return response
}

/** Fetch `path`, decoding a JSON body or throwing `ApiError`. */
export async function requestJson<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return (await (await request(path, options)).json()) as T
}

function withBearer(options: RequestOptions, token: string): RequestOptions {
  return { ...options, headers: { ...options.headers, Authorization: `Bearer ${token}` } }
}

/**
 * `POST /auth/refresh` — the one request `tokenStore` cannot make for itself.
 *
 * Deliberately raw: sending a bearer here would recurse through `authedJson`,
 * and the refresh token in the body is the whole credential anyway.
 */
const refreshTransport: RefreshTransport = (refreshToken) =>
  requestJson<AuthResponse>('/auth/refresh', {
    method: 'POST',
    body: { refresh_token: refreshToken },
  })

/**
 * Refresh the session now, returning the identity the server confirmed — the
 * only endpoint that reports `email_verified` (§5), and so the one the session
 * bootstrap and the verify interstitial both go through.
 *
 * Single-flight with every other refresh in the tab, because it is the same
 * shared promise `authedJson` awaits.
 */
export function refreshSession(): Promise<AuthResponse | null> {
  return refreshTokens(refreshTransport)
}

/**
 * The bearer this tab should send now, refreshing first if it is stale.
 *
 * `authed`'s first half, exported because `sse.ts` needs the same token on a
 * request it cannot route through `request()` — a stream is read frame by
 * frame, so there is no response body to replay after a 401. It refreshes
 * *before* sending instead, which is the same guarantee for a stream that is
 * about to run for a minute.
 */
export async function freshBearer(): Promise<string> {
  const token = await ensureFreshToken(refreshTransport)
  if (token === null) throw new ApiError(UNAUTHORIZED, {}, 'signed out')
  return token
}

/**
 * A request that carries the bearer, refreshing once and replaying once on a 401 (§4).
 * A second 401 after a successful refresh is an answer, not a stale token: the session ends.
 */
export async function authed(path: string, options: RequestOptions = {}): Promise<Response> {
  // The whole of the `anonymous` option: not a weaker `authed`, but the plain
  // `request` — so there is no path on which a bearer is attached or a refresh
  // is attempted for a caller that asked to be a stranger (5.9.26t AC13).
  // Since 11.9.26i §5 that request carries the *other* credential, and has
  // its own refresh-then-replay: `deviceReplay.ts`.
  if (options.anonymous === true) return await anonymousRequest(path, options)

  const token = await freshBearer()

  try {
    return await request(path, withBearer(options, token))
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== UNAUTHORIZED) throw error
    const fresh = await refreshAfterUnauthorized(refreshTransport, token)
    if (fresh === null) throw error
    return await replayOnce(path, withBearer(options, fresh))
  }
}

async function replayOnce(path: string, options: RequestOptions): Promise<Response> {
  try {
    return await request(path, options)
  } catch (error) {
    if (error instanceof ApiError && error.status === UNAUTHORIZED) signOutLocally()
    throw error
  }
}

/** {@link authed}, decoding a JSON body. */
export async function authedJson<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return (await (await authed(path, options)).json()) as T
}
